/* ==========================================================================
   Landing page behaviour
   --------------------------------------------------------------------------
   No dependencies. Everything is idempotent and re-runs on shopify:section:load
   so the theme editor stays live. Accordions are native <details>, so there is
   deliberately no accordion code here.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ready(el) {
    if (el.dataset.lpReady === 'true') return true;
    el.dataset.lpReady = 'true';
    return false;
  }

  var CONFIGURE_HASH = '#lp-configure';

  /* Resolves a #hash to an element. If a link points at the configurator but
     nothing carries that id — the 3D app block was placed in a plain Apps
     section, or lp-configurator was switched off — fall back to whichever
     section holds an app block, so the link still lands on the configurator. */
  function resolveAnchor(hash) {
    if (!hash || hash.length < 2 || hash.charAt(0) !== '#') return null;

    var el = document.getElementById(hash.slice(1));
    if (el) return el;

    if (hash === CONFIGURE_HASH) {
      var appBlock = document.querySelector('.lp-page [id^="shopify-block-"]');
      if (appBlock) return appBlock.closest('.shopify-section') || appBlock.parentElement;
    }

    return null;
  }

  function scrollToTarget(target) {
    target.scrollIntoView({
      behavior: reduceMotion.matches ? 'auto' : 'smooth',
      block: 'start'
    });
  }

  /* ---------------------------------------------------------------- rail --
     Smooth-scrolls anchor links, tracks the active section, and drives the
     spine tick by writing its offset and height onto the nav element. */
  function initRail(root) {
    var rail = root.querySelector('[data-lp-rail]');
    if (!rail) return;

    var nav = rail.querySelector('[data-lp-rail-nav]');
    var links = Array.prototype.slice.call(rail.querySelectorAll('[data-lp-rail-link]'));
    var toggle = rail.querySelector('[data-lp-rail-toggle]');

    // Listeners bind once; the observer below is rebuilt on every call so that a
    // section reloaded by the theme editor is picked up by the scrollspy.
    var firstRun = !ready(rail);

    if (firstRun && toggle) {
      toggle.addEventListener('click', function () {
        var open = rail.classList.toggle('lp-rail--open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.classList.toggle('lp-rail-locked', open);
      });
    }

    function closeDrawer() {
      rail.classList.remove('lp-rail--open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('lp-rail-locked');
    }

    function markActive(link) {
      links.forEach(function (other) {
        var isActive = other === link;
        other.classList.toggle('lp-rail__link--active', isActive);
        if (isActive) {
          other.setAttribute('aria-current', 'true');
        } else {
          other.removeAttribute('aria-current');
        }
      });
      if (nav && link) {
        nav.style.setProperty('--lp-spine-y', link.offsetTop + 'px');
        nav.style.setProperty('--lp-spine-h', link.offsetHeight + 'px');
        nav.classList.add('lp-rail__nav--tracking');
      }
    }

    var anchorLinks = links.filter(function (link) {
      var href = link.getAttribute('href') || '';
      return href.charAt(0) === '#' && href.length > 1;
    });

    if (firstRun) anchorLinks.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var target = resolveAnchor(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        closeDrawer();
        scrollToTarget(target);
        markActive(link);
      });
    });

    // Scrollspy. Whichever observed section is nearest the top of the viewport
    // and still on screen owns the rail.
    var targets = anchorLinks
      .map(function (link) {
        var el = resolveAnchor(link.getAttribute('href'));
        return el ? { el: el, link: link } : null;
      })
      .filter(Boolean);

    if (rail.lpObserver) {
      rail.lpObserver.disconnect();
      rail.lpObserver = null;
    }

    if (!targets.length || !('IntersectionObserver' in window)) return;

    var visible = new Map();
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            visible.set(entry.target, entry.boundingClientRect.top);
          } else {
            visible.delete(entry.target);
          }
        });

        var best = null;
        var bestTop = Infinity;
        visible.forEach(function (top, el) {
          if (Math.abs(top) < bestTop) {
            bestTop = Math.abs(top);
            best = el;
          }
        });

        if (!best) return;
        var match = targets.find(function (t) {
          return t.el === best;
        });
        if (match) markActive(match.link);
      },
      { rootMargin: '-10% 0px -55% 0px', threshold: 0 }
    );

    targets.forEach(function (t) {
      observer.observe(t.el);
    });

    rail.lpObserver = observer;
  }

  /* -------------------------------------------------------------- tracks --
     Prev/next buttons for the horizontal scroll-snap rows. */
  function initTracks(root) {
    root.querySelectorAll('[data-lp-track-root]').forEach(function (wrapper) {
      if (ready(wrapper)) return;

      var track = wrapper.querySelector('[data-lp-track]');
      var prev = wrapper.querySelector('[data-lp-track-prev]');
      var next = wrapper.querySelector('[data-lp-track-next]');
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var styles = window.getComputedStyle(track);
        return first.getBoundingClientRect().width + parseFloat(styles.columnGap || styles.gap || 0);
      }

      function sync() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= max;
      }

      function scrollBy(direction) {
        track.scrollBy({
          left: step() * direction,
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* --------------------------------------------------------------- video --
     Media bands ship a poster image and swap in the real video on click, so a
     page with six bands does not load six videos up front. */
  function initVideo(root) {
    root.querySelectorAll('[data-lp-video]').forEach(function (band) {
      if (ready(band)) return;

      var trigger = band.querySelector('[data-lp-video-trigger]');
      var holder = band.querySelector('[data-lp-video-holder]');
      if (!trigger || !holder) return;

      trigger.addEventListener('click', function () {
        var markup = holder.querySelector('template');
        if (!markup) return;
        holder.appendChild(markup.content.cloneNode(true));
        markup.remove();
        band.classList.add('lp-media-band--playing');
        var video = holder.querySelector('video');
        if (video) {
          var playback = video.play();
          if (playback && typeof playback.catch === 'function') playback.catch(function () {});
        }
      });
    });
  }

  /* -------------------------------------------------------------- reveal --
     Sections fade and rise into place as they are reached. The .lp-reveal class
     is applied here rather than in the markup, so with JavaScript unavailable
     every section renders plainly visible. */
  var revealObserver = null;

  /* Observes each element rather than its section. Observing the section meant a
     band taller than the viewport played its whole animation the moment its top
     edge appeared — so anything further down had already finished before it was
     seen — and a single missed observation left the entire section sitting at
     opacity 0. Per element, a miss costs one element, not a screen. */
  function revealer() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('lp-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );
    return revealObserver;
  }

  /* Last resort. If anything is still hidden while sitting in the viewport —
     an observer that never fired, a browser that throttled it — show it. Content
     must never be permanently invisible because an animation did not run. */
  function revealSafetyNet() {
    if (document.documentElement.dataset.lpRevealNet === 'true') return;
    document.documentElement.dataset.lpRevealNet = 'true';

    var sweep = function () {
      document.querySelectorAll('.lp-reveal:not(.lp-in)').forEach(function (el) {
        var box = el.getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) el.classList.add('lp-in');
      });
    };

    window.addEventListener('load', function () { window.setTimeout(sweep, 1200); });
    window.addEventListener('resize', sweep, { passive: true });
  }

  /* What counts as one thing arriving on screen. A card is a unit; the image and
     label inside it are not, or they would animate twice. */
  var REVEAL_UNITS = [
    '.lp-overline',
    '.lp-h2',
    '.lp-h3',
    '.lp-body',
    '.lp-small',
    '.lp-btn',
    '.lp-track-nav',
    '.lp-media',
    '.lp-strip__card',
    '.lp-specs__row',
    '.lp-specs__foot',
    '.lp-steps__item',
    '.lp-acc__item',
    '.lp-cta__panel',
    '.lp-compare__card',
    '.lp-pcar__card',
    '.lp-narrative__col',
    '.lp-configurator__panel'
  ].join(',');

  function revealRole(el) {
    if (el.matches('.lp-media')) return 'media';
    if (el.matches('.lp-h2')) return 'mask';
    return '';
  }

  function initReveal(root) {
    if (reduceMotion.matches) return;
    var observer = revealer();
    if (!observer) return;

    // The configurator's panel is a reveal unit, but the third-party 3D canvas
    // inside it is never touched — it measures itself on load.
    root.querySelectorAll('.lp-section:not(.lp-hero):not(.lp-footer)').forEach(function (section) {
      if (section.dataset.lpReveal === 'true') return;
      section.dataset.lpReveal = 'true';

      var found = Array.prototype.slice.call(section.querySelectorAll(REVEAL_UNITS));

      // Keep only the outermost of any nested matches, so a card animates as one
      // piece rather than the card and everything inside it separately.
      var units = found.filter(function (el) {
        return !found.some(function (other) {
          return other !== el && other.contains(el);
        });
      });

      units.forEach(function (el, i) {
        var role = revealRole(el);
        el.classList.add('lp-reveal');
        if (role) el.classList.add('lp-reveal--' + role);
        el.style.setProperty('--lp-reveal-delay', Math.min(i * 70, 420) + 'ms');
        observer.observe(el);
      });
    });

    revealSafetyNet();
  }

  /* ----------------------------------------------------------- accordions --
     <details> gives the semantics and works without JavaScript; it just cannot
     animate. This drives the height by hand and defers the close until the
     transition has finished. */
  function initAccordions(root) {
    root.querySelectorAll('.lp-acc__item').forEach(function (item) {
      var summary = item.querySelector('.lp-acc__summary');
      var panel = item.querySelector('.lp-acc__panel');
      if (!summary || !panel || summary.dataset.lpAcc === 'true') return;
      summary.dataset.lpAcc = 'true';

      summary.addEventListener('click', function (event) {
        if (reduceMotion.matches) return;
        event.preventDefault();
        if (item.dataset.lpAnimating === 'true') return;
        item.dataset.lpAnimating = 'true';

        var opening = !item.open;
        if (opening) item.open = true;

        var from = opening ? 0 : panel.scrollHeight;
        var to = opening ? panel.scrollHeight : 0;
        var settled = false;

        function finish() {
          if (settled) return;
          settled = true;
          panel.removeEventListener('transitionend', onEnd);
          panel.classList.remove('lp-acc__panel--animating');
          panel.style.height = '';
          if (!opening) item.open = false;
          item.dataset.lpAnimating = 'false';
        }

        function onEnd(e) {
          if (e.propertyName === 'height') finish();
        }

        panel.style.height = from + 'px';
        panel.classList.add('lp-acc__panel--animating');
        void panel.offsetHeight;
        panel.addEventListener('transitionend', onEnd);
        panel.style.height = to + 'px';

        // Belt and braces: if the transition never fires, do not leave the item
        // stuck mid-animation.
        window.setTimeout(finish, 900);
      });
    });
  }

  /* ----------------------------------------------------------------- hero --
     One orchestrated arrival rather than a scroll reveal: the image settles out
     of an overscale, then the caption bar and its contents follow. CSS holds the
     staging; this only starts it, on the frame after paint. */
  function initHeroEntrance(root) {
    var hero = root.querySelector('.lp-hero');
    if (!hero || hero.dataset.lpHero === 'true') return;
    hero.dataset.lpHero = 'true';

    if (reduceMotion.matches) {
      hero.classList.add('lp-hero--ready');
      return;
    }

    // Arm first: this installs the start states. Without this class the hero
    // renders plainly, so a failed script never leaves it invisible.
    hero.classList.add('lp-hero--armed');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        hero.classList.add('lp-hero--ready');
      });
    });
  }

  /* Snapping is switched on from here so it can be kept out of the theme
     editor, where it makes the preview awkward to scroll. */
  function initSnap() {
    if (!document.body.classList.contains('lp-page')) return;
    if (window.Shopify && window.Shopify.designMode) return;
    document.documentElement.classList.add('lp-snap');
  }

  /* ------------------------------------------------------- in-page links --
     Any anchor link outside the rail — the hero's Customize badge and button,
     the specifications "Customisation options" link, a CTA card — scrolls
     rather than jumps. Delegated, so it covers links added later by the theme
     editor without re-binding. */
  function initAnchorLinks() {
    if (document.documentElement.dataset.lpAnchors === 'true') return;
    document.documentElement.dataset.lpAnchors = 'true';

    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a[href^="#"]');
      if (!link || link.hasAttribute('data-lp-rail-link')) return;
      if (!link.closest('.lp-page')) return;

      var target = resolveAnchor(link.getAttribute('href'));
      if (!target) return;

      event.preventDefault();
      scrollToTarget(target);
    });
  }

  function init(root) {
    var scope = root || document;
    initAnchorLinks();
    initRail(scope);
    initTracks(scope);
    initVideo(scope);
    initAccordions(scope);
    initHeroEntrance(scope);
    initReveal(scope);
    initSnap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    // The rail lives in its own section group; a reloaded content section still
    // needs the spy to pick up its new anchor target.
    init(document);
  });
})();
